import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCategories, setCategoriesLoading, setCategoriesError, setSelectedCategory } from '../../../redux/slices/classSlice';
import classService from '../services/classService';
import './ClassTiles.css';

const ClassCategory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const fetchingRef = useRef(false);
  
  // Redux state
  const { categories: categoriesCache, categoriesLoading, categoriesError, selectedCategory, selectedCenter } = useSelector((state) => state.class);
  const centerInfo = useSelector((state) => state.center);
  
  // Get categories from cache
  const categories = categoriesCache[centerId] || [];
  const loading = categoriesLoading;
  const error = categoriesError;

  useEffect(() => {
    if (!centerId) {
      dispatch(setCategoriesError('No center selected'));
      dispatch(setCategoriesLoading(false));
      return;
    }

    // Check if data is already cached before making API call
    const cachedCategories = categoriesCache[centerId];
    if (cachedCategories && cachedCategories.length > 0) {
      return; // Data already loaded, skip API call
    }

    // Check if already fetching
    if (fetchingRef.current) {
      return;
    }

    fetchCategories();
  }, [centerId]);

  const fetchCategories = async () => {
    fetchingRef.current = true;
    try {
      dispatch(setCategoriesLoading(true));
      const data = await classService.getCategoriesByCenter(centerId);
      dispatch(setCategories({ centerId, categories: data || [] }));
      dispatch(setCategoriesError(null));
    } catch (err) {
      dispatch(setCategoriesError('Failed to load categories. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setCategoriesLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleBack = () => {
    navigate('/class/center');
  };

  const handleCategorySelect = (category) => {
    // Save selected category to Redux
    dispatch(setSelectedCategory(category));
    navigate(`/class/selection?centerId=${centerId}&categoryId=${category.category_Id}`);
  };

  return (
    <div className="class-category-page min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-16">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-2">
                Select a Category
              </h1>
              <p className="text-sm text-gray-500">Choose from our diverse range of class categories</p>
            </div>
            
            <button
              onClick={handleBack}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-full transition shadow-md hover:shadow-lg flex items-center gap-2"
            >
              ← Back
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="mt-4 text-gray-600">Loading categories...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Categories Grid */}
        {!loading && categories.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {categories.map((category, index) => (
              <div
                key={category.category_Id}
                className={`item-card cursor-pointer ${
                  selectedCategory?.category_Id === category.category_Id ? 'selected' : ''
                }`}
                onClick={() => handleCategorySelect(category)}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Selection Tick Mark */}
                {selectedCategory?.category_Id === category.category_Id && (
                  <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                    <svg className="w-5 h-5 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                
                {/* Animated gradient border effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-700 -z-10"></div>
                
                {/* Shine effect on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </div>

                {/* Image Section with Fixed Aspect Ratio */}
                <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
                  {category.category_Img_Url ? (
                    <>
                      <img
                        src={category.category_Img_Url.trim()}
                        alt={category.category_Name}
                        className="absolute inset-0 w-full h-full object-contain p-8 group-hover:scale-110 group-hover:rotate-2 transition-all duration-700 ease-out filter group-hover:brightness-110"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden absolute inset-0 w-full h-full items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 animate-gradient-xy">
                        <span className="text-white text-7xl drop-shadow-2xl animate-bounce">📚</span>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 animate-gradient-xy">
                      <span className="text-white text-7xl drop-shadow-2xl animate-bounce">📚</span>
                    </div>
                  )}
                  
                  {/* Animated gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/30 via-purple-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Category badge with better styling - Right side centered */}
                  {category.classCount > 0 && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 bg-white text-gray-700 px-4 py-2 rounded-full text-sm font-bold shadow-lg border border-gray-200 group-hover:scale-110 transition-transform duration-300">
                      {category.classCount} {category.classCount !== 1 ? 'Classes' : 'Class'}
                    </div>
                  )}
                </div>
                
                {/* Content Section */}
                <div className="p-6 bg-gradient-to-b from-white to-gray-50/50">
                  <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2 leading-snug">
                    {category.category_Name}
                  </h3>
                  
                  {category.category_Description && (
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {category.category_Description}
                    </p>
                  )}
                  
                  {category.class_desc && (
                    <p className="text-gray-500 text-xs mb-4 leading-relaxed">
                      {category.class_desc}
                    </p>
                  )}
                  
                  <button className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-full border border-gray-200 transition shadow-sm hover:shadow-md text-sm">
                    Explore Classes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && categories.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <p className="text-gray-600 text-lg">No categories available for this center.</p>
            <button
              onClick={handleBack}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition"
            >
              Back to Centers
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassCategory;
